if (!customElements.get('product-bottom-bar')) {
  customElements.define(
    'product-bottom-bar',
    class ProductBottomBar extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.curDocEle = window.document.documentElement;
      }

      connectedCallback() {
        this.deliveryTipDom = this.querySelector('.delivery-tip');
        this.deliveryTipDom?.querySelector('.close-icon')?.addEventListener('click', () => {
          this.deliveryTipDom.style.display = 'none';
        });

        this.buyButtonFormDom = this.getBuyButtonAnchor();
        this.headerDom = this.curDocEle.querySelector('.shopify-section--header');
        this.variantInput = this.querySelector('input.product-variant-id');
        this.backToDropDom = this.curDocEle.querySelector('.back-to-top');
        this.mainProductInfo = this.getMainProductInfo();

        this.initShadePicker();

        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.checkShowBottomBar();

        this.variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          this.handleVariantChange
        );
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('load', this.refreshInitialShadePicker);
        document.removeEventListener('click', this.handleDocumentClick);
        document.removeEventListener('keydown', this.handleDocumentKeydown);
        this.variantChangeUnsubscriber?.();
        if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
      }

      onScroll = () => {
        if (this.scrollRaf) return;
        this.scrollRaf = requestAnimationFrame(() => {
          this.scrollRaf = null;
          this.checkShowBottomBar();
        });
      };

      getBuyButtonAnchor = () => {
        return (
          this.curDocEle.querySelector('.product__block--buy_buttons .product-form__buttons') ||
          this.curDocEle.querySelector('.product__block--buy_buttons') ||
          this.curDocEle.querySelector('.product__block--variant_picker')
        );
      };

      getMainProductInfo = () => {
        return (
          document.querySelector('main product-info[id^="MainProduct-"]') ||
          document.querySelector('main product-info') ||
          document.querySelector('product-info')
        );
      };

      initShadePicker = () => {
        this.shadePicker = this.querySelector('[data-bottom-bar-shade-picker]');
        if (!this.shadePicker) return;

        this.shadeTrigger = this.shadePicker.querySelector('.product-bottom-bar__shade-trigger');
        this.shadeOptions = this.shadePicker.querySelector('.product-bottom-bar__shade-options');
        this.pcsOptions = this.shadePicker.querySelector('.product-bottom-bar__pcs-options');
        this.pcsOptionPosition = Number(this.shadePicker.dataset.pcsOptionPosition || 0);
        this.shadeOptionPosition = Number(this.shadePicker.dataset.optionPosition || 0);
        this.productVariants = this.getBottomBarVariants();

        this.shadeTrigger?.addEventListener('click', this.toggleShadePicker);
        this.shadeOptions?.addEventListener('click', this.handleShadeSelection);
        this.pcsOptions?.addEventListener('click', this.handlePcsSelection);
        document.addEventListener('click', this.handleDocumentClick);
        document.addEventListener('keydown', this.handleDocumentKeydown);
        this.refreshInitialShadePicker();
        requestAnimationFrame(this.refreshInitialShadePicker);
        window.addEventListener('load', this.refreshInitialShadePicker, { once: true });
      };

      refreshInitialShadePicker = () => {
        this.syncShadeAssetsFromMainPicker();
        this.syncPcsPicker();
        this.syncSelectedShadeTrigger();
      };

      getBottomBarVariants = () => {
        const variantsJson = this.querySelector('[data-bottom-bar-variants]')?.textContent;
        if (!variantsJson) return [];

        try {
          return JSON.parse(variantsJson);
        } catch (error) {
          return [];
        }
      };

      getSelectedPcsValue = () => {
        return this.pcsOptions
          ?.querySelector('.product-bottom-bar__pcs-option[aria-pressed="true"]')
          ?.dataset.optionValue || '';
      };

      getTargetVariantForPcs = (pcsValue) => {
        if (!pcsValue || !this.pcsOptionPosition || !this.shadeOptionPosition) return null;

        const pcsIndex = this.pcsOptionPosition - 1;
        const shadeIndex = this.shadeOptionPosition - 1;
        const selectedShade = this.shadeOptions
          ?.querySelector('.product-bottom-bar__shade-option[aria-selected="true"]')
          ?.dataset.optionValue;
        const matchingVariants = this.productVariants.filter(
          (variant) => variant.options?.[pcsIndex] === pcsValue
        );

        return (
          matchingVariants.find(
            (variant) => variant.available && variant.options?.[shadeIndex] === selectedShade
          ) ||
          matchingVariants.find((variant) => variant.available) ||
          matchingVariants.find((variant) => variant.options?.[shadeIndex] === selectedShade) ||
          matchingVariants[0] ||
          null
        );
      };

      syncCompatibleShadeOptions = (pcsValue) => {
        if (!this.pcsOptions || !pcsValue || !this.productVariants.length) return;

        const pcsIndex = this.pcsOptionPosition - 1;
        const shadeIndex = this.shadeOptionPosition - 1;

        this.shadeOptions?.querySelectorAll('.product-bottom-bar__shade-option').forEach((option) => {
          const shadeValue = option.dataset.optionValue;
          const matchingVariants = this.productVariants.filter(
            (variant) =>
              variant.options?.[pcsIndex] === pcsValue &&
              variant.options?.[shadeIndex] === shadeValue
          );

          option.hidden = matchingVariants.length === 0;
          option.disabled = matchingVariants.length > 0 && !matchingVariants.some((variant) => variant.available);
        });
      };

      syncPcsPicker = (variant = null) => {
        if (!this.pcsOptions) return;

        const selectedPcsValue =
          variant?.options?.[this.pcsOptionPosition - 1] || this.getSelectedPcsValue();
        let selectedPcsButton = null;

        this.pcsOptions.querySelectorAll('.product-bottom-bar__pcs-option').forEach((button) => {
          const isSelected = button.dataset.optionValue === selectedPcsValue;
          button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
          if (isSelected) selectedPcsButton = button;
        });

        const selectedPcsLabel = this.querySelector('[data-bottom-bar-selected-pcs]');
        if (selectedPcsLabel && selectedPcsButton) {
          selectedPcsLabel.textContent = selectedPcsButton.dataset.optionValue || '';
        }

        this.syncCompatibleShadeOptions(selectedPcsValue);
      };

      openShadePicker = () => {
        if (!this.shadePicker || !this.shadeOptions || !this.shadeTrigger) return;

        this.shadePicker.classList.add('is-open');
        this.shadeOptions.hidden = false;
        this.shadeTrigger.setAttribute('aria-expanded', 'true');

        requestAnimationFrame(() => {
          const selectedOption = this.shadeOptions.querySelector('[aria-selected="true"]');
          if (!selectedOption) return;
          this.shadeOptions.scrollTop = Math.max(
            0,
            selectedOption.offsetTop - (this.shadeOptions.clientHeight - selectedOption.offsetHeight) / 2
          );
        });
      };

      closeShadePicker = ({ restoreFocus = false } = {}) => {
        if (!this.shadePicker || !this.shadeOptions || !this.shadeTrigger) return;

        this.shadePicker.classList.remove('is-open');
        this.shadeOptions.hidden = true;
        this.shadeTrigger.setAttribute('aria-expanded', 'false');
        if (restoreFocus) this.shadeTrigger.focus({ preventScroll: true });
      };

      toggleShadePicker = () => {
        if (this.shadePicker?.classList.contains('is-open')) {
          this.closeShadePicker();
        } else {
          this.openShadePicker();
        }
      };

      handleDocumentClick = (event) => {
        if (this.shadePicker?.classList.contains('is-open') && !this.shadePicker.contains(event.target)) {
          this.closeShadePicker();
        }
      };

      handleDocumentKeydown = (event) => {
        if (event.key === 'Escape' && this.shadePicker?.classList.contains('is-open')) {
          this.closeShadePicker({ restoreFocus: true });
        }
      };

      handleShadeSelection = (event) => {
        const selectedButton = event.target.closest('.product-bottom-bar__shade-option');
        if (!selectedButton || selectedButton.disabled) return;

        const optionValueId = selectedButton.dataset.optionValueId;
        if (!optionValueId) return;

        this.mainProductInfo = this.getMainProductInfo();
        const mainVariantSelects = this.mainProductInfo?.querySelector('variant-selects');
        if (!mainVariantSelects) return;

        const matchingInputs = Array.from(
          mainVariantSelects.querySelectorAll(`input[data-option-value-id="${optionValueId}"]`)
        );
        const availableInputs = matchingInputs.filter(
          (input) => !input.disabled && !input.classList.contains('disabled')
        );
        const matchingInput = availableInputs[0];
        const matchingOption = mainVariantSelects.querySelector(
          `option[data-option-value-id="${optionValueId}"]`
        );
        let sourceControl = matchingInput || matchingOption;

        if (matchingInput) {
          availableInputs.forEach((input) => {
            input.checked = true;
          });
        } else if (matchingOption) {
          const select = matchingOption.closest('select');
          if (!select) return;
          Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
          matchingOption.selected = true;
          matchingOption.setAttribute('selected', 'selected');
          select.value = matchingOption.value;
        } else {
          return;
        }

        const selectedOptionValues = Array.from(
          new Set(
            mainVariantSelects.selectedOptionValues ||
              Array.from(
                mainVariantSelects.querySelectorAll('select option[selected], fieldset input:checked')
              ).map(({ dataset }) => dataset.optionValueId)
          )
        );

        this.shadeTrigger.dataset.productUrl =
          sourceControl.dataset.productUrl || this.mainProductInfo.dataset.url || '';
        this.shadeTrigger.setAttribute('aria-busy', 'true');
        this.closeShadePicker();

        FoxTheme.pubsub.publish(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          {
            data: {
              event: { target: mainVariantSelects },
              target: this.shadeTrigger,
              selectedOptionValues,
              from: 'productBottomBar',
            },
          }
        );
      };

      handlePcsSelection = (event) => {
        const selectedButton = event.target.closest('.product-bottom-bar__pcs-option');
        if (!selectedButton || selectedButton.disabled) return;
        if (selectedButton.getAttribute('aria-pressed') === 'true') return;

        const optionValueId = selectedButton.dataset.optionValueId;
        const pcsValue = selectedButton.dataset.optionValue;
        if (!optionValueId || !pcsValue) return;

        this.mainProductInfo = this.getMainProductInfo();
        const mainVariantSelects = this.mainProductInfo?.querySelector('variant-selects');
        if (!mainVariantSelects) return;

        const matchingInput = Array.from(
          mainVariantSelects.querySelectorAll(`input[data-option-value-id="${optionValueId}"]`)
        ).find((input) => !input.disabled && !input.classList.contains('disabled'));
        const matchingOption = mainVariantSelects.querySelector(
          `option[data-option-value-id="${optionValueId}"]`
        );
        const sourceControl = matchingInput || matchingOption;
        if (!sourceControl) return;

        if (matchingInput) {
          matchingInput.checked = true;
        } else {
          const select = matchingOption.closest('select');
          if (!select) return;
          Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
          matchingOption.selected = true;
          matchingOption.setAttribute('selected', 'selected');
          select.value = matchingOption.value;
        }

        const selectedOptionValues = Array.from(mainVariantSelects.selectedOptionValues || []);
        selectedOptionValues[this.pcsOptionPosition - 1] = optionValueId;

        const targetVariant = this.getTargetVariantForPcs(pcsValue);
        const targetShadeValue = targetVariant?.options?.[this.shadeOptionPosition - 1];
        const targetShadeButton = Array.from(
          this.shadeOptions.querySelectorAll('.product-bottom-bar__shade-option')
        ).find((option) => option.dataset.optionValue === targetShadeValue);
        if (targetShadeButton?.dataset.optionValueId) {
          selectedOptionValues[this.shadeOptionPosition - 1] = targetShadeButton.dataset.optionValueId;
        }

        this.shadeTrigger.dataset.productUrl =
          sourceControl.dataset.productUrl || this.mainProductInfo.dataset.url || '';
        this.shadeTrigger.setAttribute('aria-busy', 'true');

        this.pcsOptions.querySelectorAll('.product-bottom-bar__pcs-option').forEach((button) => {
          button.setAttribute('aria-pressed', button === selectedButton ? 'true' : 'false');
        });
        this.syncPcsPicker();

        FoxTheme.pubsub.publish(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          {
            data: {
              event: { target: mainVariantSelects },
              target: this.shadeTrigger,
              selectedOptionValues: selectedOptionValues.filter(Boolean),
              from: 'productBottomBar',
            },
          }
        );
      };

      syncShadeAssetsFromMainPicker = () => {
        if (!this.shadeOptions) return;

        this.mainProductInfo = this.getMainProductInfo();
        const mainVariantSelects = this.mainProductInfo?.querySelector('variant-selects');
        if (!mainVariantSelects) return;

        this.shadeOptions.querySelectorAll('.product-bottom-bar__shade-option').forEach((option) => {
          const optionValueId = option.dataset.optionValueId;
          const sourceInput = mainVariantSelects.querySelector(
            `input[data-option-value-id="${optionValueId}"]`
          );
          const sourcePickerSwatch = mainVariantSelects.querySelector(
            `variant-select-picker .option[data-option-value-id="${optionValueId}"] .color-block`
          );
          const sourceLabel = sourceInput
            ? Array.from(mainVariantSelects.querySelectorAll('label')).find(
                (label) => label.htmlFor === sourceInput.id
              )
            : null;
          const sourceSwatch = sourcePickerSwatch || sourceLabel?.querySelector('.swatch-color');
          const targetSwatch = option.querySelector('.product-bottom-bar__shade-swatch');
          const sourceBackground = sourceSwatch?.style.getPropertyValue('--swatch-background');

          if (sourceBackground && targetSwatch) {
            targetSwatch.style.setProperty('--swatch-background', sourceBackground);
          }
        });
      };

      syncSelectedShadeTrigger = () => {
        const selectedOption = this.shadeOptions?.querySelector('[aria-selected="true"]');
        if (!selectedOption) return;

        const selectedSwatch = selectedOption.querySelector('.product-bottom-bar__shade-swatch');
        const triggerSwatch = this.querySelector('[data-bottom-bar-selected-swatch]');
        const triggerLabel = this.querySelector('[data-bottom-bar-selected-label]');

        if (selectedSwatch && triggerSwatch) {
          triggerSwatch.setAttribute('style', selectedSwatch.getAttribute('style') || '');
        }
        if (triggerLabel) triggerLabel.textContent = selectedOption.dataset.optionValue || '';
      };

      syncShadePicker = (html, variant) => {
        if (!this.shadePicker || !variant) {
          this.shadeTrigger?.removeAttribute('aria-busy');
          return;
        }

        const optionPosition = Number(this.shadePicker.dataset.optionPosition || 0);
        const selectedValue = variant.options?.[optionPosition - 1];
        const responseVariantSelects = html?.querySelector('variant-selects');
        let selectedOption = null;

        this.shadeOptions?.querySelectorAll('.product-bottom-bar__shade-option').forEach((option) => {
          const isSelected = option.dataset.optionValue === selectedValue;
          option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
          if (isSelected) selectedOption = option;

          if (responseVariantSelects && !this.pcsOptions) {
            const optionValueId = option.dataset.optionValueId;
            const responseControls = Array.from(
              responseVariantSelects.querySelectorAll(
                `input[data-option-value-id="${optionValueId}"], option[data-option-value-id="${optionValueId}"]`
              )
            );
            if (responseControls.length) {
              option.disabled = responseControls.every(
                (responseControl) =>
                  responseControl.disabled ||
                  responseControl.classList.contains('disabled') ||
                  responseControl.classList.contains('visually-disabled')
              );
            }
          } else if (!this.pcsOptions && this.productVariants.length) {
            const shadeIndex = optionPosition - 1;
            const matchingVariants = this.productVariants.filter(
              (entry) => entry.options?.[shadeIndex] === option.dataset.optionValue
            );
            option.disabled =
              matchingVariants.length > 0 &&
              !matchingVariants.some((entry) => entry.available);
          }
        });

        this.syncPcsPicker(variant);

        if (selectedOption) {
          this.syncSelectedShadeTrigger();
        }

        this.shadeTrigger?.removeAttribute('aria-busy');
      };

      checkShowBottomBar = () => {
        const bottomBarDom = this;
        const diffBuffer = 50;
        let buyButtonFormDom = this.buyButtonFormDom;
        if (!buyButtonFormDom || !buyButtonFormDom.isConnected) {
          buyButtonFormDom = this.getBuyButtonAnchor();
          this.buyButtonFormDom = buyButtonFormDom;
        }

        if (!this.backToDropDom) {
          this.backToDropDom = this.curDocEle.querySelector('.back-to-top');
        }
        if (!buyButtonFormDom) return;

        const buyButtonFormDomRect = buyButtonFormDom.getBoundingClientRect();
        const minTop = 60;
        const shouldShow =
          this.curDocEle.clientHeight - diffBuffer < buyButtonFormDomRect.top ||
          buyButtonFormDomRect.bottom < minTop + diffBuffer;

        if (shouldShow === this.isBottomBarVisible) return;
        this.isBottomBarVisible = shouldShow;

        if (shouldShow) {
          bottomBarDom.style.bottom = '0';
          if (this.backToDropDom) this.backToDropDom.style.bottom = '110px';
        } else {
          bottomBarDom.style.bottom = '-200px';
          if (this.backToDropDom) this.backToDropDom.style.bottom = '1.5rem';
          this.closeShadePicker();
        }
      };

      handleVariantChange = (event) => {
        const { html, variant, sectionId } = event.data ?? {};
        this.mainProductInfo = this.getMainProductInfo();
        const mainSectionId = this.mainProductInfo?.dataset.section;
        if (mainSectionId && sectionId !== mainSectionId) return;

        const addButtonUpdated = html?.getElementById(`ProductSubmitButton-${sectionId}`);

        if (!this.variantInput) {
          this.variantInput = this.querySelector('input.product-variant-id');
        }

        if (variant) {
          this.variantInput.value = variant.id;
          this.variantInput.setAttribute('value', variant.id);
          this.variantInput.disabled = !variant.available;
        } else if (this.variantInput) {
          this.variantInput.disabled = true;
        }

        this.toggleAddButton(
          !variant || (addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : !variant.available),
          FoxTheme.variantStrings.soldOut
        );
        this.syncShadePicker(html, variant);
      };

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = this.querySelector(`#product-form-${this.sectionId}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = this.decoded(text);
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = this.decoded(FoxTheme.variantStrings.addToCart);
        }

        if (!modifyClass) return;
      }

      decoded(text) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = text;
        return tempDiv.textContent;
      }
    }
  );
}
